// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// actions
import { RamActions } from '../actions';

// apis
import { riskAssessmentManagementApi } from '../apis/riskAssessmentManagement.api';

// models
import type {
  ReqRAManagementDeleteProps,
  ReqRAManagementRegisterProps,
  ReqRAManagementSearchProps,
  ResRAManagementAttachmentsProps,
  ResRAManagementDetailProps,
  ResRAManagementRegisterProps,
  ResRAManagementSearchProps
} from '../models';
import type { ResultError } from 'modules/common';

// utils
import { filterValidEntries } from 'utils';

/** 위험성 평가서 목록 조회 */
export const getRAManagement = createAsyncThunk<
  ResRAManagementSearchProps,
  ReqRAManagementSearchProps,
  {
    rejectValue: ResultError;
  }
>(
  RamActions.GET_RISK_ASSESSMENT_MANAGEMENT,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const query = filterValidEntries(data) as ReqRAManagementSearchProps;

      const res = await riskAssessmentManagementApi.getRiskAssessmentManagement(query);

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험성 평가서 상세 조회 */
export const getRAManagementDetail = createAsyncThunk<
  ResRAManagementDetailProps,
  ReqRAManagementDeleteProps,
  {
    rejectValue: ResultError;
  }
>(
  RamActions.GET_RISK_ASSESSMENT_MANAGEMENT_DETAIL,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await riskAssessmentManagementApi.getRiskAssessmentManagementDetail(
        data.riskAssessmentId
      );

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험성 평가서 등록 */
export const postRAMangementRegister = createAsyncThunk<
  ResRAManagementRegisterProps,
  ReqRAManagementRegisterProps,
  { rejectValue: ResultError }
>(RamActions.POST_RISK_ASSESSMENT_MANAGEMENT, async (data, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append('issue', JSON.stringify(data.issue));

    for (let i = 0; i < data.files.length; i++) {
      formData.append('files', data.files[i]);
    }

    const res = await riskAssessmentManagementApi.postRiskAssessmentManagement(formData);
    return res;
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});

/** 위험성 평가서 삭제 */
export const deleteRAManagement = createAsyncThunk<
  void,
  ReqRAManagementDeleteProps[],
  { rejectValue: ResultError }
>(RamActions.DELETE_RISK_ASSESSMENT_MANAGEMENT, async (data, { rejectWithValue }) => {
  try {
    await Promise.all(
      data.map((item) =>
        riskAssessmentManagementApi.deleteRiskAssessmentManagement(item.riskAssessmentId)
      )
    );

    return;
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});

export const getRiskAssessmentManagementAttachments = createAsyncThunk<
  ResRAManagementAttachmentsProps,
  number,
  { rejectValue: ResultError }
>(
  RamActions.GET_RISK_ASSESSMENT_MANAGEMENT_ATTACHMENTS,
  async (riskAssessmentId, { rejectWithValue }) => {
    try {
      const res =
        await riskAssessmentManagementApi.getRiskAssessmentManagementAttachments(riskAssessmentId);
      return res;
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

export const downloadRiskAssessmentManagementAttachment = createAsyncThunk<
  void,
  string,
  { rejectValue: ResultError }
>(
  RamActions.DOWNLOAD_RISK_ASSESSMENT_MANAGEMENT_ATTACHMENT,
  async (fileId, { rejectWithValue }) => {
    try {
      await riskAssessmentManagementApi.downloadRiskAssessmentManagementAttachment(fileId);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);
